import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, Wand2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const btn = "inline-flex items-center gap-2 min-h-[36px] px-3 border font-sans text-[0.65rem] uppercase tracking-[0.2em] transition-colors disabled:opacity-40";

export default function ChapterIntroRow({ intro, onSaved }) {
  const [script, setScript] = useState(intro.script);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const sid = intro.section_id;
  const words = script.trim() ? script.trim().split(/\s+/).length : 0;

  const act = async (kind, fn, ok) => {
    setBusy(kind);
    try {
      onSaved((await fn()).data);
      toast.success(ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy("");
    }
  };
  const upload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("audio", f);
    act("upload", () => api.post(`/admin/chapter-intro/${sid}/upload`, fd), "Intro uploaded.");
  };

  return (
    <details className="border-t border-obsidian-600 py-2" data-testid={`chapter-intro-${sid}`}>
      <summary className="cursor-pointer font-sans text-xs uppercase tracking-[0.22em] text-bone-300 py-2">
        {intro.title} · <span className={intro.available ? "text-terracotta" : "text-bone-500"} data-testid={`chapter-intro-status-${sid}`}>
          {intro.available ? (intro.source === "uploaded" ? "uploaded" : "recorded") : "not recorded"}
        </span>
      </summary>
      {intro.available && <audio controls src={`${BACKEND}${intro.audio_url}`} className="max-w-full my-3" data-testid={`chapter-intro-audio-${sid}`} />}
      <Textarea value={script} onChange={(e) => setScript(e.target.value)} maxLength={400} rows={3}
        className="bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100 mt-2 mb-1" data-testid={`chapter-intro-script-${sid}`} />
      <p className="font-sans text-xs text-bone-500 mb-3">{words} words · about {Math.round(words / 2.5)}s spoken</p>
      <div className="flex gap-2 flex-wrap mb-2">
        <button onClick={() => act("gen", () => api.put(`/admin/chapter-intro/${sid}`, { script }), "Intro recorded in your voice.")}
          disabled={!!busy || script.trim().length < 10} data-testid={`chapter-intro-generate-${sid}`}
          className={`${btn} border-terracotta bg-terracotta text-obsidian-900 hover:bg-terracotta-deep`}>
          {busy === "gen" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          {intro.available ? "Re-record" : "Record in my voice"}
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid={`chapter-intro-upload-${sid}`}
          className={`${btn} border-bone-500 text-bone-100 hover:border-terracotta hover:text-terracotta`}>
          {busy === "upload" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload audio
        </button>
        <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={upload} data-testid={`chapter-intro-file-${sid}`} />
        {intro.available && (
          <button onClick={() => window.confirm("Remove this intro?") && act("del", () => api.delete(`/admin/chapter-intro/${sid}`), "Intro removed.")}
            disabled={!!busy} data-testid={`chapter-intro-delete-${sid}`}
            className={`${btn} border-obsidian-600 text-bone-400 hover:text-terracotta`}>
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        )}
      </div>
    </details>
  );
}
